import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { useNotify, usePermissions } from 'react-admin';
import { getTimeoffRequests, cancelTimeoffRequest, getHasSubordinates, TimeoffRequest } from '../dataProvider';

export const TimeoffList: React.FC = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const { permissions } = usePermissions<string[]>();
    const [requests, setRequests] = useState<TimeoffRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<number | null>(null);
    const [hasSubordinates, setHasSubordinates] = useState(false);

    const canManageApprovals = hasSubordinates || (Array.isArray(permissions) && permissions.includes('timeoff.approve_all'));
    const canViewAll = Array.isArray(permissions) && permissions.includes('timeoff.view_all_requests');

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const data = await getTimeoffRequests();
            setRequests(data);
        } catch (error) {
            console.error('Error fetching timeoff requests:', error);
            notify('Failed to load requests', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    useEffect(() => {
        getHasSubordinates().then(setHasSubordinates).catch((err) => {
            console.error('Failed to check subordinates:', err);
        });
    }, []);

    const handleCancelRequest = async () => {
        if (!selectedRequest) return;

        try {
            await cancelTimeoffRequest(selectedRequest);
            notify('Request cancelled successfully', { type: 'success' });
            fetchRequests();
        } catch (error: any) {
            const errorMessage = error?.body?.message || 'Failed to cancel request';
            notify(errorMessage, { type: 'error' });
        } finally {
            setCancelDialogOpen(false);
            setSelectedRequest(null);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Approved': return 'success';
            case 'Rejected': return 'error';
            case 'Cancelled': return 'default';
            case 'Pending': return 'warning';
            default: return 'default';
        }
    };

    const formatPartialDay = (partial: string) => {
        switch (partial) {
            case 'FirstHalf': return 'First Half';
            case 'SecondHalf': return 'Second Half';
            default: return 'Full Day';
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">
                        My Time-Off Requests
                    </Typography>
                    <Box display="flex" gap={1}>
                        {canManageApprovals && (
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/timeoff/approvals')}
                            >
                                Manage Approvals
                            </Button>
                        )}
                        {canViewAll && (
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/timeoff/all-requests')}
                            >
                                All Requests
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/timeoff/balances')}
                        >
                            View Balances
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/timeoff/create')}
                        >
                            Apply for Time Off
                        </Button>
                    </Box>
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Leave Type</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Work Days</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No time-off requests found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map((request) => (
                                    <TableRow key={request.id}>
                                        <TableCell>{request.leave_type}</TableCell>
                                        <TableCell>
                                            {new Date(request.start_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                                            {request.start_partial !== 'Full' && (
                                                <Typography variant="caption" display="block">
                                                    ({formatPartialDay(request.start_partial)})
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(request.end_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                                            {request.end_partial !== 'Full' && (
                                                <Typography variant="caption" display="block">
                                                    ({formatPartialDay(request.end_partial)})
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{request.work_days}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={request.status}
                                                color={getStatusColor(request.status) as any}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {(request.status === 'Pending' || (
                                                request.status === 'Approved' &&
                                                request.start_date >= new Date().toISOString().slice(0, 10)
                                            )) && (
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        setSelectedRequest(request.id);
                                                        setCancelDialogOpen(true);
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>

            <Dialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
            >
                <DialogTitle>Cancel Time-Off Request</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to cancel this time-off request?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)}>
                        No, Keep It
                    </Button>
                    <Button onClick={handleCancelRequest} color="error" autoFocus>
                        Yes, Cancel Request
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

